import { Link } from "@tanstack/react-router";
import { ArrowLeft, Sparkles, ChevronLeft, MapPin } from "lucide-react";
import heroImg from "@/assets/bazzar3d-hero.webp";
import { useLang } from "@/i18n/LanguageProvider";

// Served from /public so we can <link rel="preload"> it with a stable URL in index.html.
const heroBg = "/hero-bg.webp";
const heroBgMobile = "/hero-bg-mobile.webp";
const heroCard = "/hero-card-mobile.webp";

export function HeroSection() {
  const { dir, t } = useLang();
  return (
    <section className="relative isolate overflow-hidden">
      <picture aria-hidden="true" className="absolute inset-0 z-0 block h-full w-full">
        <source media="(max-width: 767px)" srcSet={heroBgMobile} />
        <img
          src={heroBg}
          alt=""
          fetchPriority="high"
          decoding="async"
          width={1920}
          height={1080}
          className="h-full w-full object-cover"
        />
      </picture>
      <div
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "linear-gradient(270deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.78) 45%, rgba(255,255,255,0.45) 80%, rgba(255,255,255,0.30) 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(55% 50% at 90% 20%, rgba(0,174,198,0.10) 0%, transparent 70%)",
        }}
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-14 pt-12 sm:px-6 lg:px-8 lg:pb-20 lg:pt-16">
        <div className="grid min-w-0 items-stretch gap-8 lg:grid-cols-2 lg:gap-10">
          <div className="relative order-2 flex min-w-0">
            <div className="relative mx-auto flex w-full max-w-[480px] flex-col">
              <div className="relative flex-1 overflow-hidden rounded-[28px] shadow-2xl ring-1 ring-black/5">
                <picture>
                  <source media="(max-width: 767px)" srcSet={heroCard} />
                  <img
                    src={heroImg}
                    alt={t("hero.imgAlt")}
                    width={640}
                    height={560}
                    decoding="async"
                    loading="lazy"
                    className="h-full min-h-[420px] w-full object-cover"
                  />
                </picture>
                <div className="absolute inset-x-4 bottom-4 flex items-center gap-3 rounded-2xl bg-[#1a1f24]/95 px-3 py-2.5 shadow-xl backdrop-blur-sm">
                  <div className="flex-1 text-start text-white">
                    <div className="text-[10px] font-semibold text-white/60">{t("hero.featured")}</div>
                    <div className="mt-0.5 flex items-center justify-start gap-1.5 text-sm font-extrabold">
                      <MapPin className="h-3.5 w-3.5 text-primary" />
                      <span>{t("hero.featuredLocation")}</span>
                    </div>
                  </div>
                  <Link
                    to="/portfolio"
                    aria-label={dir === "rtl" ? "عرض كل المشاريع" : "View all projects"}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-lg ring-4 ring-white/10 transition hover:scale-105"
                  >
                    <ChevronLeft className={`h-5 w-5 ${dir === "ltr" ? "rotate-180" : ""}`} strokeWidth={2.5} />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 flex min-w-0 flex-col text-start">
            <span className="inline-flex items-center gap-2 text-[12px] font-bold text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              {t("hero.studio")}
            </span>

            <h1 className="mt-5 max-w-full text-[2.25rem] font-extrabold leading-[1.18] text-foreground sm:text-5xl lg:text-[3.5rem]">
              {t("hero.title.line1")}
              <br />
              <span className="text-gradient-primary">{t("hero.title.line2")}</span>
              <br />
              <span className="text-gradient-primary">{t("hero.title.line3")}</span>
            </h1>

            <p className="mt-6 max-w-full text-sm leading-8 text-muted-foreground sm:max-w-xl sm:text-base">
              {t("hero.desc.full")}
            </p>

            <div className="mt-8 grid w-full grid-cols-1 gap-3 sm:flex sm:flex-wrap sm:items-center sm:justify-start">
              <Link
                to={"/quote" as any}
                className="group inline-flex h-12 min-w-0 items-center justify-center gap-2 rounded-full bg-primary px-5 text-sm font-bold text-primary-foreground shadow-[0_12px_30px_-10px_rgba(0,174,198,0.6)] transition-all hover:-translate-y-0.5 hover:bg-primary-dark hover:shadow-[0_18px_40px_-12px_rgba(0,174,198,0.75)] sm:px-7"
              >
                <ArrowLeft className={`h-4 w-4 transition-transform group-hover:-translate-x-1 ${dir === "ltr" ? "rotate-180" : ""}`} />
                {t("hero.cta.quote")}
              </Link>
              <Link
                to={"/portfolio" as any}
                className="inline-flex h-12 min-w-0 items-center justify-center gap-2 rounded-full border border-border bg-white px-5 text-sm font-bold text-foreground transition-all hover:-translate-y-0.5 hover:border-primary hover:text-primary hover:shadow-md sm:px-7"
              >
                {t("hero.cta.browseProjects")}
              </Link>
            </div>

            <div className="mt-auto pt-12">
              <div className="grid min-w-0 grid-cols-3 divide-x divide-border/70 rounded-2xl border border-border/70 bg-white/70 p-4 shadow-[0_10px_40px_-20px_rgba(0,0,0,0.2)] backdrop-blur-md [direction:ltr] sm:p-5">
                {[
                  { v: "98%", l: t("hero.stat.satisfaction") },
                  { v: "8", l: t("hero.stat.yearsExp") },
                  { v: "500+", l: t("hero.stat.completed") },
                ].map((s) => (
                  <div key={s.l} className="px-2 text-center">
                    <div
                      className="bg-gradient-to-b from-primary to-primary-dark bg-clip-text text-3xl font-black tracking-tight text-transparent sm:text-4xl"
                      dir="ltr"
                    >
                      {s.v}
                    </div>
                    <div className="mt-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {s.l}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
